import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

// Helper function to hash PIN using bcrypt via Supabase RPC
async function hashPinWithBcrypt(pin: string): Promise<string> {
  const { data, error } = await supabase.rpc('hash_pin', { p_pin: pin });
  if (error) throw new Error('Failed to hash PIN');
  return data as string;
}

// Helper function to log audit events
async function logAuditEvent(
  action: string,
  entityType: string,
  entityId: string | null,
  oldValues: any,
  newValues: any
) {
  try {
    await supabase.from('audit_logs').insert({
      action,
      entity_type: entityType,
      entity_id: entityId,
      old_values: oldValues,
      new_values: newValues,
    });
  } catch (error) {
    console.error('Failed to log audit event:', error);
  }
}

export interface TeamMember {
  id: string;
  organization_id: string;
  first_name: string | null;
  last_name: string | null;
  email?: string;
  username?: string | null;
  avatar_url: string | null;
  role: 'superadmin' | 'primary_admin' | 'owner' | 'admin' | 'manager' | 'employee';
  is_active: boolean;
  pin_hash: string | null;
  daily_target: number;
  weekly_target: number;
  monthly_target: number;
  last_active_at: string | null;
  created_at: string;
  updated_at: string;
  location_access?: LocationAccess[];
  designated_by?: string | null;
  designated_at?: string | null;
}

export interface LocationAccess {
  id: string;
  location_id: string;
  location_name?: string;
  permissions: Record<string, any>;
}

export interface CreateStaffInput {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role: 'primary_admin' | 'owner' | 'admin' | 'manager' | 'employee';
  pin?: string;
  location_ids?: string[];
}

export interface UpdateStaffInput {
  first_name?: string;
  last_name?: string;
  role?: 'primary_admin' | 'owner' | 'admin' | 'manager' | 'employee';
  is_active?: boolean;
  pin?: string;
  avatar_url?: string;
}

export interface SalesTargetsInput {
  daily_target?: number;
  weekly_target?: number;
  monthly_target?: number;
}

export function useTeam() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all team members in the organization
  const { data: teamMembers, isLoading, error, refetch } = useQuery({
    queryKey: ['team', profile?.organization_id, profile?.role],
    queryFn: async () => {
      if (!profile?.organization_id) return [];

      const currentUserRole = profile.role;
      const currentOrgId = profile.organization_id;

      // Get all profiles in the organization
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .eq('organization_id', currentOrgId)
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Get location access for each user
      const userIds = profiles.map(p => p.id);
      const { data: locationAccess } = await supabase
        .from('user_location_access')
        .select('*, location:locations(name)')
        .in('user_id', userIds);

      // Get emails from auth.users (requires admin access)
      // This will only work for admins due to RLS

      // Combine the data
      let members: TeamMember[] = profiles.map(p => ({
        ...p,
        daily_target: p.daily_target ?? 0,
        weekly_target: p.weekly_target ?? 0,
        monthly_target: p.monthly_target ?? 0,
        last_active_at: p.last_active_at ?? null,
        location_access: locationAccess
          ?.filter(la => la.user_id === p.id)
          .map(la => ({
            id: la.id,
            location_id: la.location_id,
            location_name: la.location?.name,
            permissions: la.permissions || {},
          })) || [],
      }));

      // Filter out superadmins if current user is not a superadmin
      // This ensures non-superadmin users cannot see superadmin accounts
      if (currentUserRole !== 'superadmin') {
        members = members.filter(m => m.role !== 'superadmin');
      }

      return members;
    },
    enabled: !!profile?.organization_id,
  });

  // Create a new staff member
  const createStaff = useMutation({
    mutationFn: async (input: CreateStaffInput) => {
      if (!profile?.organization_id) {
        throw new Error('No organization found');
      }

      // Get the current session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the edge function to create staff (uses service role key)
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-staff`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            email: input.email,
            password: input.password,
            first_name: input.first_name,
            last_name: input.last_name,
            role: input.role,
            organization_id: profile.organization_id,
            location_ids: input.location_ids,
            pin: input.pin,
          }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to create staff member');
      }

      return result.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  // Update a staff member
  const updateStaff = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateStaffInput }) => {
      // Get current profile for audit log
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single();

      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.first_name !== undefined) updateData.first_name = updates.first_name;
      if (updates.last_name !== undefined) updateData.last_name = updates.last_name;
      if (updates.role !== undefined) updateData.role = updates.role;
      if (updates.is_active !== undefined) updateData.is_active = updates.is_active;
      if (updates.avatar_url !== undefined) updateData.avatar_url = updates.avatar_url;
      if (updates.pin !== undefined) {
        updateData.pin_hash = updates.pin ? await hashPinWithBcrypt(updates.pin) : null;
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Log the action
      await logAuditEvent('update', 'staff', id, currentProfile, data);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  // Update sales targets for a staff member (inline editing)
  const updateSalesTargets = useMutation({
    mutationFn: async ({ id, targets }: { id: string; targets: SalesTargetsInput }) => {
      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      if (targets.daily_target !== undefined) updateData.daily_target = targets.daily_target;
      if (targets.weekly_target !== undefined) updateData.weekly_target = targets.weekly_target;
      if (targets.monthly_target !== undefined) updateData.monthly_target = targets.monthly_target;

      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await logAuditEvent('update_targets', 'staff', id, null, targets);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  // Toggle staff active status
  const toggleStaffActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ is_active: isActive, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await logAuditEvent(isActive ? 'reactivate' : 'deactivate', 'staff', id, null, null);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  // Deactivate a staff member
  const deactivateStaff = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await logAuditEvent('deactivate', 'staff', id, null, null);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  // Reactivate a staff member
  const reactivateStaff = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await logAuditEvent('reactivate', 'staff', id, null, null);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  // Update location access for a staff member
  const updateLocationAccess = useMutation({
    mutationFn: async ({ 
      userId, 
      locationIds 
    }: { 
      userId: string; 
      locationIds: string[]; 
    }) => {
      // Remove existing access
      await supabase
        .from('user_location_access')
        .delete()
        .eq('user_id', userId);

      // Add new access
      if (locationIds.length > 0) {
        const records = locationIds.map(locationId => ({
          user_id: userId,
          location_id: locationId,
          permissions: { all: true },
        }));

        const { error } = await supabase
          .from('user_location_access')
          .insert(records);

        if (error) throw error;
      }

      await logAuditEvent('update_access', 'staff', userId, null, { location_ids: locationIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  // Get a single team member
  const getTeamMember = async (id: string): Promise<TeamMember | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return null;

    const { data: locationAccess } = await supabase
      .from('user_location_access')
      .select('*, location:locations(name)')
      .eq('user_id', id);

    return {
      ...data,
      daily_target: data.daily_target ?? 0,
      weekly_target: data.weekly_target ?? 0,
      monthly_target: data.monthly_target ?? 0,
      last_active_at: data.last_active_at ?? null,
      location_access: locationAccess?.map(la => ({
        id: la.id,
        location_id: la.location_id,
        location_name: la.location?.name,
        permissions: la.permissions || {},
      })) || [],
    };
  };

  // Designate a user as primary admin (business owner) - superadmin only
  const designatePrimaryAdmin = useMutation({
    mutationFn: async ({ userId, organizationId }: { userId: string; organizationId: string }) => {
      const { data, error } = await supabase.rpc('designate_primary_admin', {
        p_user_id: userId,
        p_organization_id: organizationId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  // Revoke primary admin status - superadmin only
  const revokePrimaryAdmin = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.rpc('revoke_primary_admin', {
        p_user_id: userId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', profile?.organization_id] });
    },
  });

  return {
    teamMembers: teamMembers ?? [],
    isLoading,
    error,
    refetch,
    createStaff,
    updateStaff,
    updateSalesTargets,
    toggleStaffActive,
    deactivateStaff,
    reactivateStaff,
    updateLocationAccess,
    getTeamMember,
    designatePrimaryAdmin,
    revokePrimaryAdmin,
  };
}
